import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CompanyService } from '../../service/company.service';
import { Company, CountryDto, CityDto, ChangePasswordDTO } from '../../models/company.model';
import { ConfirmationnDialogComponent } from '../../modal/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AlertService } from '../../service/alert.service';
import { PasswordAlertService } from '../../service/password-alert.service';
import Swal from 'sweetalert2';
import { PasswordValidationComponent } from "../../shared/password-validation/password-validation.component";
import { PasswordAlertMessageComponent } from "../../shared/password-alert-message/password-alert-message.component";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { AlertMessageComponent } from "../../shared/alert-message/alert-message.component";
import { MatIcon } from "@angular/material/icon";
import { FormsModule, ɵInternalFormsSharedModule } from "@angular/forms";
import {MatFormFieldControl, MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-profile',
  standalone: true,
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
  imports: [PasswordValidationComponent, CommonModule , PasswordAlertMessageComponent, MatProgressSpinner, AlertMessageComponent, MatIcon, ɵInternalFormsSharedModule, MatFormFieldModule, MatSelectModule, MatAutocompleteModule, FormsModule, MatButtonModule, MatInputModule, TranslateModule]
})

export class ProfileComponent implements OnInit{

  @ViewChild('logoInput') logoInput!: ElementRef<HTMLInputElement>;

  constructor(private companyService: CompanyService, private dialog: MatDialog,
              private alertService: AlertService, private passwordAlertService: PasswordAlertService, private translate: TranslateService) {}
  
  isLoading = false;
  companyData: Company | null = null;
  oldPassword: string = '';
  newPassword: string = '';
  confirmNewPassword: string = '';
  editing = false;
  logoPreview: string | ArrayBuffer | null = null;
  selectedLogoFile: File | null = null;
  selectedBadgeFile: File | null = null;
  isNewPasswordValid: boolean = false;

  // Country and City data
  countries: CountryDto[] = [];
  cities: CityDto[] = [];
  filteredCities: CityDto[] = [];
  sectors: any[] = [];
  citySearch: string = ''; 
  selectedCountry: CountryDto | null = null;

  openFileDialog() {
  this.logoInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      console.log('File selezionato:', input.files[0].name);
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    
    // Load countries and cities
    this.companyService.getCountries().subscribe({
      next: (data) => {
        this.countries = data;
      },
      error: (err) => console.error('Error loading countries', err)
    });

    this.companyService.getSectors().subscribe({
      next: (data) => {
        this.sectors = data;
      },
      error: (err) => console.error('Error loading sectors', err)
    });

    this.companyService.getCompany().subscribe({
      next: (data) => {
        this.companyData = data;
        this.selectedCountry = data.country ?? data.city?.country ?? null;
        this.companyData.country = this.selectedCountry;

        // Filter cities based on selected country
        if (this.selectedCountry) {
          this.loadCitiesForCountry(this.selectedCountry.id);
        }

        // Set initial city search text
        this.citySearch = data.city?.name || '';

        if (data.logoBase64) {
          this.logoPreview = `data:image/png;base64,${data.logoBase64}`;
        } else {
          this.logoPreview = null;
        }

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Errore nel recupero dati azienda:', err);
        this.isLoading = false;
      }
    });
  }

  displayFn(item: any): string {
  if (!item) return '';
  return typeof item === 'string' ? item : item.name;
  }

  onCountryChange(): void {
    if (this.companyData) {
      // Reset city when country changes
      this.companyData.country = this.selectedCountry;
      this.companyData.city = null;
      this.citySearch = '';

      if (this.selectedCountry) {
        this.loadCitiesForCountry(this.selectedCountry.id);
      } else {
        this.cities = [];
        this.filteredCities = [];
      }
    }
  }

  compareCountries(c1: CountryDto | null, c2: CountryDto | null): boolean {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  }

  compareCities(c1: CityDto | null, c2: CityDto | null): boolean {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  }

  // Filter the cities as the user types in the city input using backend search
  onCityInput(value: string): void {
    if (this.companyData?.city && value !== this.companyData.city.name) {
      this.companyData.city = null;
    }

    if (!this.selectedCountry) {
      this.filteredCities = [];
      return;
    }

    const countryId = this.selectedCountry.id;
    const search = (value || '').trim();

    if (!search) {
      // If input is empty, show cached cities for the selected country
      this.filteredCities = this.cities.filter(city => city.country?.id === countryId);
      return;
    }

    this.companyService.getCitiesSearch(countryId, search).subscribe({
      next: (data) => {
        this.filteredCities = data;
      },
      error: (err) => {
        console.error('City search error', err);
        this.filteredCities = [];
      }
    });
  }

  private loadCitiesForCountry(countryId: number): void {
    this.companyService.getCitiesSearch(countryId, '').subscribe({
      next: (data) => {
        this.cities = data;
        this.filteredCities = data;
      },
      error: (err) => {
        console.error('Error loading cities for country', err);
        this.cities = [];
        this.filteredCities = [];
      }
    });
  }

  onCitySelected(city: CityDto): void {
    if (this.companyData) {
      this.companyData.city = city;
      this.selectedCountry = city.country ?? this.selectedCountry;
      this.companyData.country = this.selectedCountry;
      this.citySearch = city.name;
    }
  }

  compareSectors(s1: any | null, s2: any | null): boolean {
    return s1 && s2 ? s1.id === s2.id : s1 === s2;
  }

  getSectorName(): string {
    if (this.companyData?.sector) {
      // Se sector è un oggetto (e non null), usa sector.id
      if (this.companyData.sector !== null && typeof this.companyData.sector === 'object' && (this.companyData.sector as any)?.id) {
        const sectorId = (this.companyData.sector as any).id;
        const foundSector = this.sectors.find(s => s.id === sectorId);
        return foundSector ? foundSector.name : '';
      }
      // Se sector è un numero, usa il valore diretto
      const sectorId = this.companyData.sector;
      const foundSector = this.sectors.find(s => s.id.toString() === sectorId.toString());
      return foundSector ? foundSector.name : '';
    }
    return '';
  }

  saveCompany(): void {

    if (!this.companyData?.name || !this.companyData?.address || 
        !this.selectedCountry || !this.companyData?.supervisor) {
      this.alertService.errorKey('profile.required-fields-error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validazione email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (this.companyData.email && !emailRegex.test(this.companyData.email)) {
      this.alertService.errorKey('profile.invalid-email-error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validazione telefono (solo numeri, spazi e +)
    const phoneRegex = /^[\d\s+]+$/;
    if (this.companyData.phone && !phoneRegex.test(this.companyData.phone)) {
      this.alertService.errorKey('profile.invalid-phone-error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validazione inclusion manager (solo lettere e spazi)
    const nameRegex = /^[a-zA-ZÀ-ÿ\s]+$/;
    if (this.companyData.supervisor && !nameRegex.test(this.companyData.supervisor)) {
      this.alertService.errorKey('profile.invalid-inclusion-manager-error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    Swal.fire({
      title: this.translate.instant('profile.confirm-save-title'),
      text: this.translate.instant('profile.confirm-save-text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('profile.confirm-save-button'),
      cancelButtonText: this.translate.instant('profile.cancel'),
      cancelButtonColor: 'red',
    }).then((result) => {
      if (result.isConfirmed && this.companyData) {
        const formData = new FormData();
        this.companyData.country = this.selectedCountry;
        
        // Log per debug
        console.log('Company data prima di mandare:', this.companyData);
        
        formData.append('company', JSON.stringify(this.companyData));

        if (this.selectedLogoFile) {
          formData.append('logoFile', this.selectedLogoFile); 
        }

        if (this.companyData.badge && this.selectedBadgeFile) {
          formData.append('badgeFile', this.selectedBadgeFile);
        }

        this.companyService.updateCompany(formData).subscribe({
          next: (updated:any) => {
            console.log(updated)
            this.companyData = updated.company;
            this.selectedLogoFile = null;
            this.selectedBadgeFile = null;
            this.editing = false;
            this.alertService.success(updated.message);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            window.location.reload();
          },
          error: (err) => {
            console.error('Errore durante il salvataggio', err);
            const message = err?.error?.message;
            if (message) {
              this.alertService.error(message);
            } else {
              this.alertService.errorKey('profile.unexpected-save-error');
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }
    });
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files[0]) {
      this.selectedLogoFile = input.files[0]; 

      const reader = new FileReader();

      reader.onload = () => {
        this.logoPreview = reader.result;
      };

      reader.readAsDataURL(this.selectedLogoFile);
    }
  }

  onBadgeFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedBadgeFile = input.files[0];
    }

    if (this.selectedBadgeFile && this.selectedBadgeFile.type !== 'application/pdf') {
    alert(this.translate.instant('profile.only-pdf-upload'));
    this.selectedBadgeFile = null;
    return;
    }

  }

  toggleEdit(): void {
    this.editing = !this.editing;
    if (this.editing && this.companyData) {
      this.citySearch = this.companyData.city?.name || '';
      if (this.selectedCountry) {
        this.loadCitiesForCountry(this.selectedCountry.id);
      }
    }
  }

  toggleCancel(): void {
    this.editing = false;
    this.selectedLogoFile = null;
    this.selectedBadgeFile = null;
    // Reload company data to discard unsaved changes
    this.ngOnInit();
  }

  downloadPdf(): void {
    if (this.companyData?.badgeDocumentBase64) {
      const byteCharacters = atob(this.companyData.badgeDocumentBase64);
      const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'badge-document.pdf'; 
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }

  toggleVisibility(): void {
    if (this.companyData) {
      this.companyData.published = !this.companyData.published;
    }
  }

  getDescriptionLength(): number {
    return this.companyData?.description ? this.companyData.description.length : 0;
  }

  onDescriptionInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (textarea.value.length > 150) {
      this.companyData!.description = textarea.value.substring(0, 150);
    }
  }

  changePasswordCompany(): void 
  {

    if (!this.oldPassword || !this.newPassword || !this.confirmNewPassword) {
      this.passwordAlertService.errorKey('profile.password-all-fields-required');
      return;
    }

    if (this.newPassword !== this.confirmNewPassword) {
      this.passwordAlertService.errorKey('profile.password-mismatch');
      return;
    }

    if (!this.isNewPasswordValid) {
      this.passwordAlertService.errorKey('profile.password-requirements-error');
      return;
    }

    const changePassDto: ChangePasswordDTO = {
      oldPassword: this.oldPassword,
      newPassword: this.newPassword
    };

    this.companyService.changePassword(this.oldPassword, this.newPassword, this.confirmNewPassword).subscribe({
      next: (response) => {
        this.passwordAlertService.successKey('profile.password-changed-success');
        // Reset fields
        this.oldPassword = '';
        this.newPassword = '';
        this.confirmNewPassword = '';
      },
      error: (err) => {
        // Fare in modo c
        const errorMessage = err.error?.message;
        if (errorMessage) {
          this.passwordAlertService.error(errorMessage);
        } else {
          this.passwordAlertService.errorKey('profile.password-change-error');
        }
      }
    });
  }

  // Permette solo numeri, spazi e +
  onPhoneKeyPress(event: KeyboardEvent): boolean {
    const char = event.key;
    const allowed = /[\d\s+]/;
    if (!allowed.test(char)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Permette solo lettere e spazi
  onNameKeyPress(event: KeyboardEvent): boolean {
    const char = event.key;
    const allowed = /[a-zA-ZÀ-ÿ\s]/;
    if (!allowed.test(char)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  deleteLogo(): void {
    this.logoPreview = null;
    this.selectedLogoFile = null;
    if (this.companyData) {
      this.companyData.logoBase64 = null;
    }
  }

  onPasswordValidityChange(isValid: boolean): void {
    this.isNewPasswordValid = isValid;
  }
}






